import React, { useState, useEffect } from 'react';
import { RefreshCw, Flag, Search, Plus, Edit2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button, Card, LoadingState, EmptyState, Modal, Input } from '@/components/common';
import {
  getAdminPriorities,
  createPriority,
  updatePriority,
  type PriorityData,
  type PriorityFormData,
} from '@/services/nomenclatoare.service';

// Culori predefinite pentru priorități
const PRESET_COLORS = [
  '#ef4444', // Roșu (Urgent)
  '#f97316', // Portocaliu (Ridicată)
  '#eab308', // Galben (Normală)
  '#22c55e', // Verde (Scăzută)
  '#1A67A3', // Albastru
  '#8b5cf6', // Violet
  '#06b6d4', // Cyan
  '#6b7280', // Gri
];

export function PrioritiesPage() {
  const [priorities, setPriorities] = useState<PriorityData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPriority, setEditingPriority] = useState<PriorityData | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState<PriorityFormData>({
    name: '',
    color: '#1A67A3',
    isDefault: false,
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const loadPriorities = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await getAdminPriorities();
      setPriorities(data);
    } catch (err) {
      console.error('Failed to load priorities:', err);
      setError('Eroare la încărcarea priorităților');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPriorities();
  }, []);

  const filteredPriorities = priorities.filter((priority) => {
    const query = searchQuery.toLowerCase();
    return priority.name.toLowerCase().includes(query);
  });

  const openCreateModal = () => {
    setEditingPriority(null);
    setFormData({
      name: '',
      color: '#1A67A3',
      isDefault: false,
      isActive: true,
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (priority: PriorityData) => {
    setEditingPriority(priority);
    setFormData({
      name: priority.name,
      color: priority.color,
      isDefault: priority.isDefault,
      isActive: priority.isActive,
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPriority(null);
    setFormData({
      name: '',
      color: '#1A67A3',
      isDefault: false,
      isActive: true,
    });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Numele priorității este obligatoriu';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      if (editingPriority) {
        await updatePriority(editingPriority.id, formData);
        toast.success('Prioritate actualizată cu succes!');
      } else {
        await createPriority(formData);
        toast.success('Prioritate creată cu succes!');
      }

      closeModal();
      loadPriorities();
    } catch (err: unknown) {
      console.error('Failed to save priority:', err);
      const errorMessage = (err as { message?: string })?.message || 'Eroare la salvarea priorității';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <LoadingState message="Se încarcă prioritățile..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-orange-500 text-white">
            <Flag className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Prioritate</h1>
            <p className="text-gray-600 mt-1">Administrare niveluri de prioritate pentru activități</p>
          </div>
        </div>
        <div className="flex gap-3">
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full font-medium self-center">
            {filteredPriorities.length} priorități
          </span>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={loadPriorities}
          >
            Reîncarcă
          </Button>
          <Button
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={openCreateModal}
          >
            Adaugă prioritate
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      <Card className="overflow-hidden">
        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Caută priorități..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {filteredPriorities.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon="list"
              title="Nu există priorități"
              description={
                searchQuery
                  ? "Nu s-au găsit priorități care să corespundă căutării."
                  : 'Nu există priorități în sistem.'
              }
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Poziție
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Nume prioritate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Culoare
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Stare
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Acțiuni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredPriorities.map((priority) => (
                  <tr key={priority.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-600">{priority.position}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: priority.color || '#6B7280' }}
                        />
                        <span className="font-medium text-gray-900">{priority.name}</span>
                        {priority.isDefault && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            Implicit
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded border border-gray-200"
                          style={{ backgroundColor: priority.color || '#6B7280' }}
                        />
                        <span className="text-sm text-gray-600 font-mono">
                          {priority.color || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {priority.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Activă
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          Inactivă
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<Edit2 className="w-4 h-4" />}
                        onClick={() => openEditModal(priority)}
                      >
                        Editează
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal pentru creare/editare */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingPriority ? 'Editare prioritate' : 'Adaugă prioritate nouă'}
        size="md"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={closeModal} disabled={isSaving}>
              Anulează
            </Button>
            <Button variant="primary" onClick={handleSubmit} isLoading={isSaving}>
              {editingPriority ? 'Salvează modificările' : 'Creează prioritate'}
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nume prioritate"
            type="text"
            placeholder="Ex: Urgent"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={formErrors.name}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Culoare</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`w-8 h-8 rounded-lg border-2 transition-all ${
                    formData.color === color
                      ? 'border-primary-500 ring-2 ring-primary-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => setFormData({ ...formData, color })}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
              />
              <input
                type="text"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                placeholder="#1A67A3"
              />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Prioritate activă</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Prioritate implicită</span>
            </label>
          </div>
        </form>
      </Modal>
    </div>
  );
}
